import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { toast } from 'react-toastify'
import type { NextUpAccent, NextUpCardData } from '../mock-data'

interface INextUpCardProps {
  card: NextUpCardData
}

// Accent palette — keys map to NextUpCardData.accent.
const ACCENT_BG: Record<NextUpAccent, string> = {
  blue: 'linear-gradient(155deg, #1E4480 0%, #0E1F3C 100%)',
  orange: 'linear-gradient(155deg, #6B3A12 0%, #2A1A0C 100%)',
  green: 'linear-gradient(155deg, #0F4A35 0%, #0A2A1E 100%)'
}

const ACCENT_TAG: Record<NextUpAccent, { bg: string; color: string }> = {
  blue: { bg: 'rgba(77,179,255,0.15)', color: '#4DB3FF' },
  orange: { bg: 'rgba(249,115,22,0.18)', color: '#F97316' },
  green: { bg: 'rgba(34,197,94,0.18)', color: '#22C55E' }
}

const ACCENT_BUTTON: Record<NextUpAccent, { bg: string; hover: string }> = {
  blue: {
    bg: 'linear-gradient(180deg, #2E8FCC 0%, #1F72A6 12.31%, #093E76 102.38%)',
    hover: 'linear-gradient(180deg, #3FA0DD 0%, #2987BC 12.31%, #0E4685 102.38%)'
  },
  orange: {
    bg: 'linear-gradient(180deg, #F97316 0%, #C2410C 100%)',
    hover: 'linear-gradient(180deg, #FB923C 0%, #DC2626 100%)'
  },
  green: {
    bg: 'linear-gradient(180deg, #10B981 0%, #065F46 100%)',
    hover: 'linear-gradient(180deg, #34D399 0%, #047857 100%)'
  }
}

const Chip = ({ children }: { children: React.ReactNode }) => (
  <Box
    px="10px"
    h="26px"
    borderRadius="6px"
    border="1px solid rgba(255,255,255,0.20)"
    bg="#070C1666"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    <Text fontSize="10px" fontWeight={600} color="white" m={0} letterSpacing="0.06em">
      {children}
    </Text>
  </Box>
)

const NextUpCard = ({ card }: INextUpCardProps) => {
  const tag = ACCENT_TAG[card.accent]
  const button = ACCENT_BUTTON[card.accent]

  return (
    <Box
      bg={ACCENT_BG[card.accent]}
      borderRadius="16px"
      p="16px"
      width={{ base: '280px', md: '300px' }}
      flexShrink={0}
      scrollSnapAlign="start"
    >
      <VStack spacing="10px" align="stretch">
        <Box
          alignSelf="flex-start"
          px="10px"
          h="22px"
          borderRadius="6px"
          bg={tag.bg}
          display="inline-flex"
          alignItems="center"
        >
          <Text color={tag.color} fontSize="10px" fontWeight={700} m={0} letterSpacing="0.10em">
            {card.tag}
          </Text>
        </Box>

        <Text color="white" fontSize="20px" fontWeight={700} m={0} lineHeight="1.2" noOfLines={2}>
          {card.title}
        </Text>
        <Text color="rgba(255,255,255,0.65)" fontSize="13px" m={0} noOfLines={1}>
          {card.byline}
        </Text>

        <HStack spacing="8px" mt="4px" flexWrap="wrap">
          {card.meta.map(m => (
            <Chip key={m}>{m}</Chip>
          ))}
        </HStack>

        <Button
          mt="6px"
          h="44px"
          w="100%"
          borderRadius="999px"
          bg={button.bg}
          color="white"
          fontSize="14px"
          fontWeight={600}
          border="none"
          _hover={{ bg: button.hover }}
          _active={{ opacity: 0.92 }}
          _focus={{ boxShadow: 'none' }}
          _focusVisible={{ boxShadow: 'none' }}
          onClick={() => toast.info(`Starting: ${card.title}`)}
        >
          {card.ctaLabel}
        </Button>
      </VStack>
    </Box>
  )
}

export default NextUpCard
